// Reusable voice-to-text button. Click to start dictation; it stays recording
// (continuous, restarts itself if the engine drops out on silence) until the
// user clicks again to stop. While recording it expands into a pill — a
// spinning square + equalizer + mm:ss timer. Each final phrase is handed to
// `onResult(text)` so the host composer can append it to its input.
//
// Native (no motion/Tailwind) so it drops into any of the plain-CSS composers.

import React, { useState, useEffect, useRef } from 'react';
import { Mic } from 'lucide-react';
import './VoiceInputButton.css';

const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export default function VoiceInputButton({ onResult, className = '' }) {
  const [listening, setListening] = useState(false);
  const [recTime, setRecTime] = useState(0);
  const recRef = useRef(null);
  const stoppingRef = useRef(false);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  // Recording timer (mm:ss) while listening.
  useEffect(() => {
    if (!listening) { setRecTime(0); return undefined; }
    const id = setInterval(() => setRecTime((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [listening]);

  // Stop recognition if the host unmounts mid-recording.
  useEffect(() => () => {
    stoppingRef.current = true;
    try { recRef.current?.stop(); } catch (_) { /* ignore */ }
  }, []);

  const stop = () => {
    stoppingRef.current = true;
    try { recRef.current?.stop(); } catch (_) { /* ignore */ }
    setListening(false);
  };

  const start = () => {
    if (listening) { stop(); return; } // toggle off

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { window.alert('Voice input is not supported in this browser.'); return; }

    const rec = new SR();
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = false;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          const t = e.results[i][0].transcript.trim();
          if (t) onResultRef.current?.(t);
        }
      }
    };
    rec.onend = () => {
      if (stoppingRef.current) { setListening(false); return; }
      try { rec.start(); } catch (_) { setListening(false); }
    };
    rec.onerror = (ev) => {
      if (ev.error === 'no-speech' || ev.error === 'aborted') return;
      stoppingRef.current = true;
      setListening(false);
    };

    recRef.current = rec;
    stoppingRef.current = false;
    rec.start();
    setListening(true);
  };

  return (
    <button
      type="button"
      className={`voice-btn${listening ? ' active' : ''}${className ? ` ${className}` : ''}`}
      onClick={start}
      aria-label={listening ? 'Stop recording' : 'Voice input'}
      title={listening ? 'Stop' : 'Voice input'}
    >
      <span className="voice-icon">
        {listening ? <span className="voice-square" /> : <Mic size={18} />}
      </span>
      {listening && (
        <span className="voice-expand">
          <span className="voice-eq" aria-hidden="true">
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} style={{ animationDelay: `${(i * 0.05).toFixed(2)}s` }} />
            ))}
          </span>
          <span className="voice-time">{formatTime(recTime)}</span>
        </span>
      )}
    </button>
  );
}
